import List from '@/components/common/List';
import { useApplication } from '@/hooks/useApplication';
import { useDialogs } from '@/hooks/useDialogs';
import { useUpdateNodes } from '@/hooks/useUpdateNodes';
import { EventState } from '@/types/eventhandler';
import { getUpdatedNodes } from '@/util/application';
import { Button, Stack } from '@mui/material';
import { ConfirmOptions } from 'material-ui-confirm';
import { FC, useState } from 'react';
import SyncEventListItem from './SyncEventListItem';
import SyncEventListItemEdit from './SyncEventListItemEdit';

type SyncEventsManagerProps = Record<string, never>;

export type SyncEventItem = {
  name: string;
  state: EventState;
};

const SyncEventsManager: FC<SyncEventsManagerProps> = () => {
  const { application, setApplication } = useApplication();
  const { setUpdateNodes } = useUpdateNodes();
  const { confirm } = useDialogs();

  const [openEdit, setOpenEdit] = useState(false);

  const confirmExists: ConfirmOptions = {
    title: 'Event already exists',
    description: `This user event already exists`,
    confirmationText: 'Ok',
    hideCancelButton: true
  };

  const handleOnSave = (newSyncEvent: SyncEventItem, oldName?: string) => {
    let updatedSyncEvents = application.syncevents ? structuredClone(application.syncevents) : [];

    if (!oldName) {
      updatedSyncEvents[updatedSyncEvents.length] = {
        name: newSyncEvent.name,
        state: newSyncEvent.state
      };
    } else {
      updatedSyncEvents = updatedSyncEvents.map((syncevent) => {
        if (syncevent.name === oldName) {
          return { name: newSyncEvent.name, state: newSyncEvent.state };
        } else {
          return syncevent;
        }
      });
    }
    if (
      updatedSyncEvents.filter(
        (syncevent) => syncevent.name.toLocaleLowerCase() === newSyncEvent.name.toLocaleLowerCase()
      ).length > 1
    ) {
      confirm(confirmExists);
      return;
    }

    setApplication({ ...application, syncevents: updatedSyncEvents });
    setUpdateNodes(getUpdatedNodes(newSyncEvent.name, 'event_name', application.blocks.details));
  };

  return (
    <Stack gap={1} alignItems='start'>
      <List sx={{ width: '100%' }}>
        {application.syncevents
          ?.sort((a, b) => a.name.localeCompare(b.name))
          .map((syncevent, index) => (
            <SyncEventListItem
              key={`prompt.${syncevent.name}.${index}`}
              syncEvent={syncevent}
              onSave={(newSyncEvent) => handleOnSave(newSyncEvent, syncevent.name)}
              onDelete={() => {
                const removed = application.syncevents?.filter((keep) => keep.name !== syncevent.name);
                setApplication({
                  ...application,
                  syncevents: removed
                });
                setUpdateNodes(getUpdatedNodes(syncevent.name, 'event_name', application.blocks.details));
              }}
            />
          ))}
      </List>
      {openEdit && (
        <SyncEventListItemEdit
          onClose={() => setOpenEdit(false)}
          onSave={(newUserEvent) => handleOnSave(newUserEvent)}
        />
      )}
      <Button variant='contained' color='primary' sx={{ ml: 2 }} onClick={() => setOpenEdit(true)}>
        Add new
      </Button>
    </Stack>
  );
};

export default SyncEventsManager;
